import {
  Hand,
  PenLine,
  SlidersHorizontal,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import type { PointerEvent, PointerEventHandler, RefObject } from "react";
import { useEffect, useRef, useState } from "react";

import type { SmileSimulationComparisonBindings } from "../hooks/useSmileSimulationComparison";
import { SmileSimulationDrawingLayer } from "./SmileSimulationDrawingLayer";

type ComparisonSliderProps = {
  comparison: {
    bindings: SmileSimulationComparisonBindings;
    value: number;
    containerRef: RefObject<HTMLDivElement | null>;
  };
  isDrawingEnabled?: boolean;
  images: {
    before: string;
    after: string;
  };
};

type ComparisonBindings = {
  containerRef: RefObject<HTMLDivElement | null>;
  onPointerCancel: PointerEventHandler<HTMLDivElement>;
  onPointerDown: PointerEventHandler<HTMLDivElement>;
  onPointerMove: PointerEventHandler<HTMLDivElement>;
  onPointerUp: PointerEventHandler<HTMLDivElement>;
  updateValue: SmileSimulationComparisonBindings["updateValue"];
};

type ComparisonInteractionMode = "compare" | "draw" | "pan";

function getBindings(
  comparison: ComparisonSliderProps["comparison"],
): ComparisonBindings {
  return {
    containerRef: comparison.containerRef,
    onPointerCancel: comparison.bindings.onPointerCancel,
    onPointerDown: comparison.bindings.onPointerDown,
    onPointerMove: comparison.bindings.onPointerMove,
    onPointerUp: comparison.bindings.onPointerUp,
    updateValue: comparison.bindings.updateValue,
  };
}

export function ComparisonSlider({
  comparison,
  isDrawingEnabled = false,
  images,
}: ComparisonSliderProps) {
  const [interactionMode, setInteractionMode] =
    useState<ComparisonInteractionMode>("compare");
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const isCompareDraggingRef = useRef(false);
  const transformedFrameRef = useRef<HTMLDivElement | null>(null);
  const panStartRef = useRef<{
    pan: { x: number; y: number };
    pointer: { x: number; y: number };
  } | null>(null);
  const {
    containerRef,
    onPointerCancel,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    updateValue,
  } = getBindings(comparison);
  const isDrawingMode = interactionMode === "draw";
  const isPanMode = interactionMode === "pan";
  const isToolModeActive = isDrawingEnabled && (isDrawingMode || isPanMode);
  const transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
  const toolButtonClass =
    "flex h-9 w-9 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-45";
  const getModeButtonClass = (mode: ComparisonInteractionMode) =>
    `${toolButtonClass} ${
      interactionMode === mode
        ? "bg-[#8F14E5] text-white"
        : "bg-white/90 text-[#2b2b2b] hover:bg-white"
    }`;

  useEffect(() => {
    if (!isDrawingEnabled) {
      setInteractionMode("compare");
    }
  }, [isDrawingEnabled]);

  const updateZoom = (nextZoom: number) => {
    const clampedZoom = Math.min(3, Math.max(1, nextZoom));

    setZoom(clampedZoom);

    if (clampedZoom === 1) {
      setPan({ x: 0, y: 0 });
      setInteractionMode((mode) => (mode === "pan" ? "compare" : mode));
    }
  };

  const handlePanPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!isPanMode) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    panStartRef.current = {
      pan,
      pointer: { x: event.clientX, y: event.clientY },
    };
  };

  const handlePanPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isPanMode || !panStartRef.current) {
      return;
    }

    event.preventDefault();
    setPan({
      x:
        panStartRef.current.pan.x +
        event.clientX -
        panStartRef.current.pointer.x,
      y:
        panStartRef.current.pan.y +
        event.clientY -
        panStartRef.current.pointer.y,
    });
  };

  const handlePanPointerUp = () => {
    panStartRef.current = null;
  };

  const updateComparisonValueFromTransformedFrame = (clientX: number) => {
    const transformedFrame = transformedFrameRef.current;

    if (!transformedFrame) {
      return;
    }

    const bounds = transformedFrame.getBoundingClientRect();

    if (bounds.width <= 0) {
      return;
    }

    updateValue(((clientX - bounds.left) / bounds.width) * 100);
  };

  const handleComparePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDrawingEnabled) {
      onPointerDown(event);
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    isCompareDraggingRef.current = true;
    updateComparisonValueFromTransformedFrame(event.clientX);
  };

  const handleComparePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (isDrawingEnabled) {
      if (!isCompareDraggingRef.current) {
        return;
      }

      event.preventDefault();
      updateComparisonValueFromTransformedFrame(event.clientX);
      return;
    }

    onPointerMove(event);
  };

  const handleComparePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDrawingEnabled) {
      onPointerUp(event);
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    isCompareDraggingRef.current = false;
  };

  const handleComparePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDrawingEnabled) {
      onPointerCancel(event);
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    isCompareDraggingRef.current = false;
  };

  return (
    <div
      ref={containerRef}
      className={`relative h-full w-full touch-none select-none ${
        isDrawingMode
          ? "cursor-crosshair"
          : isPanMode
            ? "cursor-grab"
            : "cursor-col-resize"
      }`}
      onPointerCancel={
        isToolModeActive ? handlePanPointerUp : handleComparePointerCancel
      }
      onPointerDown={
        isToolModeActive ? handlePanPointerDown : handleComparePointerDown
      }
      onPointerMove={
        isToolModeActive ? handlePanPointerMove : handleComparePointerMove
      }
      onPointerUp={
        isToolModeActive ? handlePanPointerUp : handleComparePointerUp
      }
    >
      <div
        ref={transformedFrameRef}
        className="absolute inset-0 origin-center"
        style={{ transform }}
      >
        <img
          src={images.after}
          alt="Smile simulation result"
          className="absolute inset-0 h-full w-full bg-[#13071f] object-contain"
        />
        <img
          src={images.before}
          alt="Captured smile preview"
          className="absolute inset-0 h-full w-full bg-[#13071f] object-contain"
          style={{
            clipPath: `inset(0 ${100 - comparison.value}% 0 0)`,
          }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 z-10"
          style={{ left: `calc(${comparison.value}% - 1px)` }}
        >
          <div className="relative h-full">
            <div className="h-full w-0.5 bg-white/90 shadow-lg" />
          </div>
        </div>
      </div>
      <SmileSimulationDrawingLayer
        isEnabled={isDrawingEnabled}
        isDrawingMode={isDrawingMode}
        pan={pan}
        targetRef={containerRef}
        zoom={zoom}
      />
      {!isDrawingEnabled ? (
        <>
          <div className="pointer-events-none absolute left-5 top-5 rounded-[0.65rem] bg-[#eeeeee] px-4 py-1.5 text-base font-extrabold text-[#2b2b2b] shadow">
            Before
          </div>
          <div className="pointer-events-none absolute right-5 top-5 rounded-[0.65rem] bg-[#8F14E5] px-4 py-1.5 text-base font-extrabold text-white shadow">
            After
          </div>
        </>
      ) : (
        <div
          className="absolute inset-x-0 top-5 z-30 flex justify-center"
          onPointerDown={(event) => event.stopPropagation()}
          onPointerMove={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <div className="flex items-center gap-1 rounded-full bg-black/55 p-1 text-xs font-semibold text-white backdrop-blur">
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => updateZoom(zoom - 0.25)}
              disabled={zoom <= 1}
              className={`${toolButtonClass} bg-white/90 text-[#2b2b2b] hover:bg-white`}
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <span className="min-w-10 text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => updateZoom(zoom + 0.25)}
              disabled={zoom >= 3}
              className={`${toolButtonClass} bg-white/90 text-[#2b2b2b] hover:bg-white`}
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Compare"
              onClick={() => setInteractionMode("compare")}
              className={getModeButtonClass("compare")}
            >
              <SlidersHorizontal className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Move"
              onClick={() => setInteractionMode("pan")}
              disabled={zoom <= 1}
              className={getModeButtonClass("pan")}
            >
              <Hand className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Draw"
              onClick={() => setInteractionMode("draw")}
              className={getModeButtonClass("draw")}
            >
              <PenLine className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
